import { StatusCode } from 'api/grpc/resources';
import { hasRegionsWithConcepts } from 'components/ModelOutputRenderer/components/utils';
import { TIAnnotationMST } from 'modules/Annotations/store/annotation';
import { generateInternalAnnotationId } from 'utils/annotations/general';
import { makeUID } from 'utils/strings/strings';
import { isBboxShapeProps, isMaskShapeProps } from 'utils/typeguards/predictionOutputs';

export type AnnotationItem = TIAnnotationMST | CF.API.Annotations.Annotation | undefined;

export type GroupedAnnotationAnnotationMSTByType = {
  trusted: AnnotationItem[];
  nonTrusted: AnnotationItem[];
};

export const NON_TRUSTED_ANNOTATION_SEPARATOR = '###';

export const getClassificationMapEntryKey = (concept: CF.API.Concept, annotation: CF.API.Annotations.Annotation) => {
  const isAnnotationTrusted = getIsAnnotationTrusted(annotation);

  // For non-trusted annotations, we create unique keys with annotation ID to ensure they're displayed
  // separately, preventing grouping of annotations that might be pending approval or from different sources.
  return isAnnotationTrusted || annotation.isTemporary ? concept.name : `${concept.name}${NON_TRUSTED_ANNOTATION_SEPARATOR}${annotation.id}`;
};

export const getRegionMapEntryKey = (region: CF.API.RegionsEntity, annotation: CF.API.Annotations.Annotation) => {
  const isAnnotationTrusted = getIsAnnotationTrusted(annotation);

  // For non-trusted annotations, we create unique keys with annotation ID to ensure they're displayed
  // separately, preventing grouping of annotations that might be pending approval or from different sources.
  return isAnnotationTrusted ? region.id : `${region.id}${NON_TRUSTED_ANNOTATION_SEPARATOR}${annotation.id}`;
};

export const createTempClassificationAnnotation = (concept?: CF.API.Concept): CF.API.Annotations.Annotation | null => {
  if (!concept) return null;

  return {
    id: makeUID(16),
    input_id: '',
    isTemporary: true,
    data: {
      concepts: [concept],
    },
  };
};

export const createTempRegionAnnotation = (baseAnnotation: CF.API.Annotations.Annotation): CF.API.Annotations.Annotation | null => {
  if (!hasRegionsWithConcepts(baseAnnotation.data) || !baseAnnotation.data.regions?.length) return null;

  const id = makeUID(16);
  const region = baseAnnotation.data.regions[0];
  const concept = region.data.concepts?.[0];
  const internalId = generateInternalAnnotationId({
    id,
    conceptId: concept?.id || '',
    regionId: region.id,
    taskId: baseAnnotation.task_id || '',
  });

  return {
    id,
    internalId,
    input_id: '',
    isTemporary: true,
    data: {
      regions: [region],
    },
  };
};

export const partitionAnnotationsByTrust = (annotations: AnnotationItem[]): GroupedAnnotationAnnotationMSTByType =>
  annotations.reduce(
    (result: GroupedAnnotationAnnotationMSTByType, annotation: AnnotationItem) => {
      if (getIsAnnotationTrusted(annotation)) {
        result.trusted.push(annotation);
      } else {
        result.nonTrusted.push(annotation);
      }
      return result;
    },
    { trusted: [], nonTrusted: [] },
  );

export const getIsAnnotationTrusted = (annotation: AnnotationItem) => {
  if (!annotation) return false;

  // If NO task_id AND worker is model or workflow, then trusted
  if (!annotation.task_id && annotation.worker && (annotation.worker.model || annotation.worker.workflow)) {
    return true;
  }

  // If has worker.user.id AND trusted is true, then trusted
  if (annotation.worker?.user?.id && annotation.trusted === true) {
    return true;
  }

  // If isSuggestion, then trusted
  if (annotation.isSuggestion) {
    return true;
  }

  // If isTemporary, then trusted
  if (annotation.isTemporary) {
    return true;
  }

  // If has task_id AND trusted is not true, then not trusted
  if (annotation.task_id && annotation.trusted !== true) {
    return false;
  }

  // All other cases, not trusted
  return false;
};

export const determineRegionInfo = (regionData: CF.API.ShapeProps) => {
  if (isBboxShapeProps(regionData)) {
    return { bounding_box: regionData.bbox };
  } else if (isMaskShapeProps(regionData)) {
    return { mask: regionData.mask };
  }
  return { polygon: (regionData as CF.API.PolygonRegionInfo).polygon };
};

export type PredictionParams = {
  inputId: string;
  regionId?: string;
  appId: string;
  userId: string;
  regionData: CF.API.ShapeProps;
  concepts?: CF.API.Concept[];
  isSuggestion?: boolean;
};

export const getNewAnnotationFromPrediction = ({
  inputId,
  regionId,
  regionData,
  concepts,
  appId,
  isSuggestion,
  userId,
}: PredictionParams): CF.API.Annotations.Annotation => ({
  id: makeUID(16),
  input_id: inputId,
  data: {
    regions: [
      {
        id: regionId || makeUID(16),
        region_info: determineRegionInfo(regionData),
        data: {
          concepts: (concepts || []).map((c) => ({
            ...c,
            id: isSuggestion ? c.name : c.id,
            name: c.name,
            app_id: appId,
          })),
        },
      },
    ],
  },
  trusted: true,
  isSuggestion: false,
  isTemporary: false,
  worker: {
    user: {
      id: userId,
    } as CF.API.Users.User,
  },
  status: {
    code: StatusCode.ANNOTATION_SUCCESS,
    description: '',
  },
});
